/**
 * 이상한 방 - 히트 프록시 (물건이 없는 자리의 판정 대체물)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 6.1절(두 종류의 히트) · 6.2절(빈 자리의 판정은 관대하게) · 6.3절(옮긴 물건은 두 자리가 다 정답)
 *
 * ## 이 파일이 없으면 '제거' 동사가 조준 불가능해진다
 *
 * 6.1절은 판정 경로를 둘로 나눈다: **오브젝트 히트**와 **빈 셀 히트**.
 * 궤도 카메라에서는 빈 셀 히트가 쉽다 - 바닥 평면과 광선을 교차시키면 셀이 나온다(`picker.ts`).
 * 1인칭 시절에는 그 경로가 통째로 무너졌고, 아이소메트릭에서도 '없어진 물건의 자리'를 바닥 히트와
 * 구별해야 하므로 프록시는 그대로 필요하다.
 *
 * 없어진 물건을 조준하면 광선은 그 뒤의 바닥이나 벽을 맞힌다. 술래가 "여기 원래 화분 있었는데"라고
 * 정확히 조준해도 판정은 "벽을 맞혔다"가 되고, 탄환 1발이 날아간다.
 * 컨셉시트 4.2가 **최고 난도**라고 지목한 동사가 6.2절이 경고한 대로
 * **"제일 짜증나는 동사"**로 전락하는 지점이 정확히 여기다.
 *
 * 그래서 제거된 오브젝트가 점유하던 자리에 **보이지 않지만 레이캐스트에는 잡히는 상자**를 남긴다.
 * 이것이 6.2절("제거된 오브젝트가 점유하던 셀 전부를 정답 영역으로 잡는다. 여유 있게")의 3D 구현체다.
 *
 * ## 옮긴 물건은 두 자리가 다 정답이다 (6.3절 - 2026-09-09, 사용자 지시로 뒤집은 규칙)
 *
 * 한때 이 파일은 **제거된 자리에만** 프록시를 세웠고, 그 이유를 "이동이 before/after 두 자리 다 정답인
 * 가장 쉬운 동사가 되면 동사별 난이도 차이가 무너진다"로 적어 두었다. 실제로 사람이 해 보니 무너진 것은
 * 난이도가 아니라 **신뢰**였다. 큰 물건이 옮겨졌을 때 눈에 먼저 들어오는 것은 새 자리가 아니라 **비어 버린
 * 옛 자리**이고, 거기를 정확히 조준해 쏜 한 발은 벽에 맞아 탄환만 잃는다. 6.2절이 제거에 대해 경고한
 * "억울한 실패"와 같은 실패이고, 사람은 그것을 난이도가 아니라 고장으로 읽는다.
 *
 * 그래서 이동에도 **옮기기 전 자리**에 프록시를 세운다. 쉬워지는 폭은 생각보다 작다 - **두 자리는 같은 한
 * 정답의 두 조준점이지 두 번의 기회가 아니다.** 판정 키가 `Change`이므로(`game/hunt.ts`) 한쪽을 맞히면
 * 다른 쪽은 `already-found`가 된다. 늘어난 것은 겨눌 면적이지 점수가 아니다.
 *
 * ## `visible = false`인데 왜 맞는가 (Three.js 동작 확인 완료)
 *
 * Three의 `Raycaster`는 `object.visible`을 **보지 않는다.** 레이어(`layers`)만 확인하고
 * 곧장 `object.raycast()`를 부른다(`three/src/core/Raycaster.js`의 `intersect()`).
 * 따라서 `visible = false`는 **렌더 비용 0 + 판정은 정상**이라는 정확히 우리가 원하는 조합이다.
 * 반투명 머티리얼로 흉내 낼 필요가 없다(그건 투명 정렬 비용과 색 오염을 부른다).
 *
 * 이 성질은 Three 버전 업그레이드로 깨질 수 있는 외부 전제이므로 `tapPicker.test.ts`('프록시가 보이지 않아도(visible=false) 판정된다')가 직접 검증한다.
 *
 * ## 언제 세우는가 - 수색 페이즈 진입 시 반드시 동기화한다
 *
 * 진입점은 `syncHitProxies(scene, original, mutated, changes, catalog)`,
 * 라운드 재시작은 `resetHitProxies(...)`다. 빠뜨리면 '제거' 동사가 통째로 조준 불가능해진다.
 * 그리고 **물건이 없는 자리에만** 세운다 - 제거된 자리와 옮기기 전 자리 둘이다(`buildHitProxySpecs` 주석).
 *
 * ## 왜 `instanceId` userData 키를 쓰지 않는가 (중요)
 *
 * `objectView.ts`는 살아 있는 오브젝트에 `instanceId`를 심고, `picker.ts`는 그 키로 선택을 해석한다.
 * 프록시에 같은 키를 쓰면 에디터 포인터가 **이미 사라진 오브젝트를 집는다** -
 * 유령을 선택해 이동·색 변경을 시도하는 경로가 열리는 셈이다.
 * 그래서 별도 키를 쓴다. 이 한 줄이 두 판정 경로를 서로 격리한다.
 */

import {
  BoxGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
} from 'three'

import { rotatedFootprint } from '../core/grid'
import type { Catalog } from '../core/room'
import {
  CELL_SIZE_METERS,
  type CatalogEntry,
  type CatalogId,
  type Cell3,
  type InstanceId,
  type RoomState,
  type Rot,
  type Verb,
} from '../core/types'
import { computeObjectCenterWorld, disposeObject3D, footprintToMeters } from './objectView'

/* --- 씬 구조 규약 ------------------------------------------------------------- */

/** 프록시를 담는 컨테이너 이름. `objectView`가 씬 이름으로 뷰 루트를 찾는 방식과 같다. */
const HIT_PROXY_ROOT_NAME = 'hit-proxies'

/** 원본(제거된) 인스턴스 id. `objectView`의 `instanceId`와 **다른 키여야 한다**(파일 상단 주석). */
const USER_DATA_ORIGIN_ID = 'hitProxyOriginId'
const USER_DATA_ORIGIN_CATALOG_ID = 'hitProxyCatalogId'
const USER_DATA_KIND = 'hitProxyKind'

/**
 * 프록시의 종류 마커. 값으로 심는다 - `true` 같은 불리언 플래그였다면 '이동' 프록시가 생긴 지금
 * 둘을 구분할 방법이 없었을 것이다. 밖으로는 `HitProxyInfo.kind`의 리터럴 타입으로만 드러난다.
 */
const HIT_PROXY_KIND_REMOVED = 'removed'

/** 옮겨지기 **전** 자리의 프록시. 6.2절의 관대함을 이동까지 넓힌 것이다(머리말 "옮긴 물건은 두 자리 다 정답"). */
const HIT_PROXY_KIND_MOVED = 'moved'

/** 프록시가 대신 서는 이유. `verb`가 아니라 별도 어휘인 것은 이 층이 게임 레이어를 모르기 때문이다. */
export type HitProxyKind = typeof HIT_PROXY_KIND_REMOVED | typeof HIT_PROXY_KIND_MOVED

/* --- 판정 영역 크기 ----------------------------------------------------------- */

/**
 * 점유 영역에서 각 축으로 더 넓히는 양(셀). 0.5셀이면 양쪽으로 총 1셀(0.25m)이 늘어난다.
 *
 * 6.2절: "여유 있게. 여기에 인색하면 웃긴 실패가 아니라 억울한 실패가 나온다."
 * 넉넉하게 잡아 생기는 손해는 "옆 오브젝트를 노렸는데 제거 정답으로 처리되는" 경우뿐인데,
 * 그것도 **정답은 정답**이다(프록시는 제거된 자리에만 존재한다). 손해가 플레이어에게 유리한 쪽으로만 난다.
 */
const PROXY_MARGIN_CELLS = 0.5

/**
 * 프록시 한 변의 최소 크기(셀). 머그컵(1×1×1 = 0.25m)의 자리를 5m 밖에서 조준하는 것은
 * 난이도가 아니라 픽셀 헌팅이다. 2셀(0.5m)이면 실내 거리에서 맨눈으로 겨눌 수 있다.
 */
const PROXY_MIN_SIZE_CELLS = 2

/* --- 타입 --------------------------------------------------------------------- */

/** 프록시 하나를 만들기 위한 최소 정보. 변화 **직전**(= `original`)의 오브젝트 상태에서 그대로 뽑아 온다. */
export type HitProxySpec = Readonly<{
  kind: HitProxyKind
  originInstanceId: InstanceId
  cell: Cell3
  entry: CatalogEntry
  rot: Rot
}>

/** 레이캐스트가 프록시를 맞혔을 때 판정 레이어가 받아 가는 정보. */
export type HitProxyInfo = Readonly<{
  kind: HitProxyKind
  originInstanceId: InstanceId
  catalogId: CatalogId
}>

/**
 * 프록시를 세울지 판단하는 데 필요한 변화 정보의 **최소 형태**.
 *
 * `game/changeGen.ts`의 `Change`가 이 모양을 그대로 만족하므로 호출부는 변화 목록을 그냥 넘기면 된다.
 * 그런데도 `Change`를 직접 import 하지 않는 이유: 렌더 레이어가 게임 레이어를 참조하기 시작하면
 * 의존 방향이 뒤집힌다(게임 → 렌더가 정상이다). 구조적 타입으로 받으면 방향을 지키면서
 * 타입 검사도 그대로 받는다.
 */
export type ProxyChangeLike = Readonly<{ verb: Verb; targetId: InstanceId }>

/* --- 크기 계산 ---------------------------------------------------------------- */

/**
 * 프록시 박스의 크기(미터).
 *
 * **회전된** footprint를 쓰고 메시 자체는 돌리지 않는다. 점유 영역이 어차피 축 정렬 박스이므로
 * (`grid.ts`의 `occupiedCells` 규약) 회전을 크기에 흡수시키면 "메시도 돌리고 크기도 돌리는"
 * 이중 적용 버그가 원천적으로 불가능해진다.
 */
function hitProxySizeMeters(
  entry: CatalogEntry,
  rot: Rot,
): { x: number; y: number; z: number } {
  const occupied = footprintToMeters(rotatedFootprint(entry.footprint, rot))
  const margin = PROXY_MARGIN_CELLS * CELL_SIZE_METERS * 2
  const minimum = PROXY_MIN_SIZE_CELLS * CELL_SIZE_METERS

  return {
    x: Math.max(occupied.x + margin, minimum),
    y: Math.max(occupied.y + margin, minimum),
    z: Math.max(occupied.z + margin, minimum),
  }
}

/* --- 생성 --------------------------------------------------------------------- */

/**
 * 히트 프록시 하나를 만든다. 씬에 붙이는 것은 `syncHitProxies`의 몫이다.
 *
 * `DoubleSide`인 이유: 제거된 소품의 자리는 이제 **걸어 들어갈 수 있는 빈 공간**이다
 * (충돌 맵은 현재 상태에서 만들어지므로 사라진 소파를 막지 않는다).
 * 소파가 있던 자리 안에 선 채로 쏘면 앞면이 등 뒤에 있으므로, 단면이면 광선이 그냥 빠져나간다.
 * 안쪽에서도 맞아야 6.2절의 "관대하게"가 성립한다.
 */
function createHitProxy(spec: HitProxySpec): Object3D {
  const { cell, entry, rot, originInstanceId } = spec
  const size = hitProxySizeMeters(entry, rot)
  const center = computeObjectCenterWorld(cell, entry, rot)

  const proxy = new Mesh(
    new BoxGeometry(size.x, size.y, size.z),
    // 그려지지 않으므로 색·조명 계산이 필요 없다. 가장 싼 머티리얼을 쓴다.
    new MeshBasicMaterial({ side: DoubleSide }),
  )
  proxy.name = `hit-proxy:${originInstanceId}`
  proxy.position.set(center.x, center.y, center.z)

  // 렌더 비용 0, 레이캐스트는 정상(파일 상단 주석: Three의 Raycaster는 visible을 보지 않는다).
  proxy.visible = false

  proxy.userData[USER_DATA_KIND] = spec.kind
  proxy.userData[USER_DATA_ORIGIN_ID] = originInstanceId
  proxy.userData[USER_DATA_ORIGIN_CATALOG_ID] = entry.id

  return proxy
}

/* --- 씬 동기화 ---------------------------------------------------------------- */

/**
 * 변화 목록에서 **물건이 없는 자리의 프록시 사양**을 뽑아낸다 - 제거된 자리와 옮기기 전 자리 둘이다.
 *
 * ## 이 둘 말고는 절대 세우지 않는다
 *
 * 회전·색 바뀜·생김에는 자리를 대신할 것이 없다. 그 물건들은 조준하면 그 자리에 그대로 있고,
 * 프록시를 세우면 **엉뚱한 자리가 정답이 되는 문**만 열린다. 이동이 예외가 된 이유는 머리말에 있다 -
 * 옮겨진 물건은 옛 자리가 비므로 조준할 것이 사라지기 때문이고, 그 둘은 같은 한 정답의 두 조준점이다.
 *
 * 씬을 만지지 않는 순수 함수로 분리해 둔 이유는 이 규칙을 렌더 없이 테스트하기 위해서다.
 *
 * @param original 변화 **직전**의 상태. 사라진 소품의 셀·회전·카탈로그도, 옮겨지기 전 자리도 여기에만 남아 있다
 * @param mutated  변경본. 제거·이동이 실제로 반영됐는지 교차 검증하는 용도다
 */
export function buildHitProxySpecs(
  original: RoomState,
  mutated: RoomState,
  changes: readonly ProxyChangeLike[],
  catalog: Catalog,
): readonly HitProxySpec[] {
  const specs: HitProxySpec[] = []

  for (const change of changes) {
    if (change.verb !== 'remove' && change.verb !== 'move') continue

    const before = original.objects.get(change.targetId)
    if (before === undefined) {
      // 원본에 없다 = 변화 목록과 스냅샷이 어긋났다. 조용히 넘기면 그 동사만 조준 불가능해지고,
      // 술래는 원인 모를 억울한 탈락을 겪는다.
      throw new Error(
        `buildHitProxySpecs: '${change.verb}' 대상 '${change.targetId}'가 original에 없다. ` +
          '변화 목록과 스냅샷이 같은 라운드의 것인지 확인하라.',
      )
    }

    const after = mutated.objects.get(change.targetId)
    if (change.verb === 'remove' && after !== undefined) {
      throw new Error(
        `buildHitProxySpecs: '${change.targetId}'는 제거 변화인데 mutated에 아직 남아 있다. ` +
          '변화 생성기가 상태를 실제로 반영하지 않았다.',
      )
    }
    if (change.verb === 'move' && after === undefined) {
      throw new Error(
        `buildHitProxySpecs: '${change.targetId}'는 이동 변화인데 mutated에 없다. ` +
          '이동이 아니라 제거가 일어났거나, 변화 목록이 다른 라운드의 것이다.',
      )
    }

    const entry = catalog.get(before.catalogId)
    if (entry === undefined) {
      throw new Error(
        `buildHitProxySpecs: 카탈로그에 없는 catalogId '${before.catalogId}' ` +
          `(인스턴스 '${before.id}'). 판정 영역 크기를 계산할 수 없다.`,
      )
    }

    specs.push({
      kind: change.verb === 'remove' ? HIT_PROXY_KIND_REMOVED : HIT_PROXY_KIND_MOVED,
      originInstanceId: before.id,
      // 이동도 **옛** 자리다. `mutated`의 셀을 쓰면 새 자리에 프록시가 겹쳐 서고, 옛 자리는 그대로 빈다.
      cell: before.cell,
      entry,
      rot: before.rot,
    })
  }

  return specs
}

/**
 * 프록시 목록을 씬에 반영한다. 없는 것은 만들고, 목록에서 빠진 것은 지운다.
 *
 * 전량 재생성하지 않는 이유는 `objectView.syncObjectViews`와 같다 -
 * 매번 지오메트리를 새로 만들면 GC 스파이크가 프레임을 끊고, 그 끊김은 이 게임에서
 * 곧 "방금 뭐가 바뀐 거지?"라는 판정 불신이 된다.
 *
 * 두 가지 호출 형태를 받는다:
 *  - `(scene, specs)` - 사양을 직접 계산해 넘기는 기존 경로
 *  - `(scene, original, mutated, changes, catalog)` - 수색 페이즈 진입 시 쓰는 경로.
 *    빈 자리만 골라 세우는 일(`buildHitProxySpecs`)을 호출부마다 다시 짜지 않게 한다
 */
export function syncHitProxies(scene: Object3D, specs: readonly HitProxySpec[]): void
export function syncHitProxies(
  scene: Object3D,
  original: RoomState,
  mutated: RoomState,
  changes: readonly ProxyChangeLike[],
  catalog: Catalog,
): void
export function syncHitProxies(
  scene: Object3D,
  specsOrOriginal: readonly HitProxySpec[] | RoomState,
  mutated?: RoomState,
  changes?: readonly ProxyChangeLike[],
  catalog?: Catalog,
): void {
  applyHitProxySpecs(scene, resolveSpecs(specsOrOriginal, mutated, changes, catalog))
}

/**
 * 프록시를 전부 버리고 제거된 자리에 다시 세운다. **라운드 재시작 경로**가 부른다.
 *
 * `syncHitProxies`의 차분 갱신으로도 결과는 같지만, 재시작은 인스턴스 id 체계 자체가 갈리는 지점이라
 * "이전 라운드의 프록시가 우연히 같은 id로 살아남는" 경우를 원천 차단하는 쪽이 싸다.
 * `clearHitProxies`가 GPU 자원까지 해제하므로 누수도 없다.
 */
export function resetHitProxies(
  scene: Object3D,
  original: RoomState,
  mutated: RoomState,
  changes: readonly ProxyChangeLike[],
  catalog: Catalog,
): void {
  clearHitProxies(scene)
  syncHitProxies(scene, original, mutated, changes, catalog)
}

/** 두 호출 형태를 하나의 사양 목록으로 정규화한다. */
function resolveSpecs(
  specsOrOriginal: readonly HitProxySpec[] | RoomState,
  mutated: RoomState | undefined,
  changes: readonly ProxyChangeLike[] | undefined,
  catalog: Catalog | undefined,
): readonly HitProxySpec[] {
  if (isSpecList(specsOrOriginal)) return specsOrOriginal

  if (mutated === undefined || changes === undefined || catalog === undefined) {
    throw new Error(
      'syncHitProxies: (scene, original, mutated, changes, catalog) 호출에 인자가 빠졌다.',
    )
  }

  return buildHitProxySpecs(specsOrOriginal, mutated, changes, catalog)
}

/** `Array.isArray`의 readonly 배열 좁히기는 TS 버전에 따라 흔들리므로 가드를 명시한다. */
function isSpecList(value: readonly HitProxySpec[] | RoomState): value is readonly HitProxySpec[] {
  return Array.isArray(value)
}

function applyHitProxySpecs(scene: Object3D, specs: readonly HitProxySpec[]): void {
  const root = resolveHitProxyRoot(scene)
  const stale = new Map<InstanceId, Object3D>()

  for (const child of root.children) {
    const originId = readStringUserData(child, USER_DATA_ORIGIN_ID)
    if (originId === undefined) continue
    stale.set(originId, child)
  }

  for (const spec of specs) {
    if (stale.delete(spec.originInstanceId)) continue
    root.add(createHitProxy(spec))
  }

  for (const orphan of stale.values()) {
    root.remove(orphan)
    disposeObject3D(orphan)
  }
}

/** 프록시를 전부 걷어내고 GPU 자원까지 해제한다. 라운드 전환 경로가 부른다. */
export function clearHitProxies(scene: Object3D): void {
  const existing = scene.getObjectByName(HIT_PROXY_ROOT_NAME)
  if (existing === undefined) return

  disposeObject3D(existing)
  existing.removeFromParent()
}

function resolveHitProxyRoot(scene: Object3D): Object3D {
  const existing = scene.getObjectByName(HIT_PROXY_ROOT_NAME)
  if (existing !== undefined) return existing

  const created = new Group()
  created.name = HIT_PROXY_ROOT_NAME
  scene.add(created)
  return created
}

/* --- 레이캐스트 역추적 --------------------------------------------------------- */

/**
 * 레이캐스트가 맞힌 Object3D가 히트 프록시인지 확인하고 원본 정보를 돌려준다.
 *
 * 부모를 거슬러 올라가는 이유는 `objectView.getInstanceIdFromObject3D`와 같다 -
 * 지금은 프록시가 자식 없는 단일 Mesh지만, 나중에 디버그 표시(반투명 윤곽 등)를 자식으로 붙이는 순간
 * 자기 자신만 보는 구현은 조용히 판정 구멍을 만든다.
 */
export function readHitProxyInfo(object: Object3D): HitProxyInfo | undefined {
  let current: Object3D | null = object

  while (current !== null) {
    const kind = readStringUserData(current, USER_DATA_KIND)
    if (isHitProxyKind(kind)) {
      const originInstanceId = readStringUserData(current, USER_DATA_ORIGIN_ID)
      const catalogId = readStringUserData(current, USER_DATA_ORIGIN_CATALOG_ID)

      // 마커는 있는데 원본 id가 없으면 판정 불가능한 유령이다.
      // 조용히 넘기면 "맞혔는데 아무 일도 안 일어나는" 최악의 증상이 되므로 시끄럽게 실패한다.
      if (originInstanceId === undefined || catalogId === undefined) {
        throw new Error(
          `readHitProxyInfo: 히트 프록시에 원본 정보가 없다 (name='${current.name}')`,
        )
      }

      return { kind, originInstanceId, catalogId }
    }

    current = current.parent
  }

  return undefined
}

/**
 * 마커 문자열이 우리가 세운 종류인가.
 *
 * **목록으로 묻는다.** 한때 이 자리가 `kind === HIT_PROXY_KIND_REMOVED` 한 줄이었는데, '이동' 프록시를
 * 더한 날 씬에는 프록시가 서고 조준하면 아무것도 없는 상태가 됐다 - 세우는 쪽만 고치고 읽는 쪽을 두면
 * 새 종류는 **조용히 안 맞는다**(예외도 로그도 없다). 종류를 늘릴 때 반드시 함께 지나야 하는 문이다.
 */
function isHitProxyKind(value: string | undefined): value is HitProxyKind {
  return value === HIT_PROXY_KIND_REMOVED || value === HIT_PROXY_KIND_MOVED
}

/** userData는 임의 값이 들어갈 수 있으므로 경계에서 타입을 확인한다. */
function readStringUserData(target: Object3D, key: string): string | undefined {
  const value: unknown = target.userData[key]
  return typeof value === 'string' ? value : undefined
}
